import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import PeopleIcon from "@mui/icons-material/People";
import FavoriteIcon from "@mui/icons-material/Favorite";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ReportIcon from "@mui/icons-material/Report";
import { ToastContainer, toast } from "react-toastify";
import { getDashboardCounts } from "../../Services/api";
import CircularProgress from "@mui/material/CircularProgress";

const Dashboard = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Color definitions matching login page
  const primaryColor = "#F4B400";
  const secondaryColor = "#0F9D58";
  const accentColor = "#DB4437";
  const darkColor = "#333333";
  const lightColor = "#F5F5F5";

  const pieColors = [primaryColor, secondaryColor, "#388e3c", accentColor];

  const dashboardCount = async () => {
    try {
      setLoading(true);
      const result = await getDashboardCounts();
      setData(result.data);
      toast.error(result.message, { position: "top-center" });
    } catch (error) {
      setLoading(false);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const dashboardData = [
    {
      title: "Total Users",
      value: data.total_users || 0,
      icon: <PeopleIcon fontSize="large" sx={{ color: primaryColor }} />,
      color: lightColor,
    },
    {
      title: "Interests Sent",
      value: data.total_interested || 0,
      icon: <FavoriteIcon fontSize="large" sx={{ color: secondaryColor }} />,
      color: lightColor,
    },
    {
      title: "Total Matches",
      value: data.total_matches || 0,
      icon: <DoneAllIcon fontSize="large" sx={{ color: "#388e3c" }} />,
      color: lightColor,
    },
    {
      title: "Reported Profiles",
      value: data.reported_profiles || 0,
      icon: <ReportIcon fontSize="large" sx={{ color: accentColor }} />,
      color: lightColor,
    },
  ];

  useEffect(() => {
    dashboardCount();
  }, []);

  return (
    <Box
      sx={{
        p: 3,
        background: `linear-gradient(135deg, ${lightColor} 0%, #ffffff 100%)`,
        minHeight: "100vh",
      }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <CircularProgress sx={{ color: primaryColor }} size={60} />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {/* Info Cards */}
            {dashboardData.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.85)",
                    borderRadius: 4,
                    height: "100%",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    backdropFilter: "blur(8px)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: `0 12px 28px ${primaryColor}20`,
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          mr: 2,
                          p: 1.5,
                          backgroundColor: `${item.icon.props.sx.color}20`,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ color: darkColor }}
                        >
                          {item.value.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: `${darkColor}90`,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontSize: "0.75rem",
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Charts Section */}
            <Grid item xs={12} mt={4}>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  color: darkColor,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  "&:before": {
                    content: '""',
                    display: "inline-block",
                    width: "20px",
                    height: "4px",
                    backgroundColor: primaryColor,
                    mr: 2,
                    borderRadius: "2px",
                  },
                }}
              >
                Analytics Overview
              </Typography>
            </Grid>

            {/* Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  backdropFilter: "blur(8px)",
                  backgroundColor: "rgba(255, 255, 255, 0.85)",
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    mb={2}
                    sx={{
                      color: darkColor,
                      fontWeight: 600,
                    }}
                  >
                    User Metrics
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={dashboardData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis
                        dataKey="title"
                        angle={-15}
                        textAnchor="end"
                        interval={0}
                        height={60}
                        tick={{ fill: darkColor, fontSize: 12 }}
                      />
                      <YAxis tick={{ fill: darkColor }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: `1px solid ${primaryColor}20`,
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                        }}
                      />

                      <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                        {dashboardData.map((entry, index) => (
                          <Cell
                            key={`bar-cell-${index}`}
                            fill={pieColors[index % pieColors.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Pie Chart - Fixed Legend Overlap */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  backdropFilter: "blur(8px)",
                  backgroundColor: "rgba(255, 255, 255, 0.85)",
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    mb={2}
                    sx={{
                      color: darkColor,
                      fontWeight: 600,
                    }}
                  >
                    Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart
                      margin={{ top: 20, right: 20, bottom: 80, left: 20 }}
                    >
                      <Pie
                        data={dashboardData}
                        dataKey="value"
                        nameKey="title"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={50}
                        paddingAngle={2}
                        label={({ percent }) =>
                          `${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {dashboardData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={pieColors[index % pieColors.length]}
                          />
                        ))}
                      </Pie>
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        wrapperStyle={{
                          paddingTop: "20px",
                          fontSize: "0.75rem",
                        }}
                        formatter={(value) => (
                          <span style={{ color: darkColor }}>{value}</span>
                        )}
                      />
                      <Tooltip
                        formatter={(value, name, props) => [
                          value,
                          props.payload.title,
                        ]}
                        contentStyle={{
                          borderRadius: "8px",
                          border: `1px solid ${primaryColor}20`,
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
      <ToastContainer
        position="top-center"
        toastStyle={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          color: darkColor,
          border: `1px solid ${primaryColor}20`,
          borderRadius: "8px",
          boxShadow: `0 4px 12px ${primaryColor}20`,
        }}
      />
    </Box>
  );
};

export default Dashboard;
